import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Edit2, Trash2, X, Loader2, Plus } from "lucide-react";

const formatDateTime = (iso) => new Date(iso).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Zurich" });

export default function AppointmentsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [types, setTypes] = useState([]);
    const [pharmacists, setPharmacists] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/appointments");
            setItems(r.data || []);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        api.get("/admin/appointment-types").then(r => setTypes(r.data || []));
        api.get("/admin/pharmacists").then(r => setPharmacists(r.data || []));
    }, [load]);

    const remove = async (a) => {
        if (!window.confirm(`Supprimer le rendez-vous de ${a.customer_name} ?`)) return;
        try {
            await api.delete(`/admin/appointments/${a.id}`);
            toast.success("Rendez-vous supprimé");
            load();
        } catch { toast.error("Échec"); }
    };

    return (
        <div data-testid="admin-appointments">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h2 className="font-display text-2xl text-brand-ink">Rendez-vous</h2>
                    <p className="font-sans text-sm text-brand-ink/60 mt-1">{items.length} au total</p>
                </div>
                <button onClick={() => setCreating(true)} className="ll-btn ll-btn-primary">
                    <Plus className="h-4 w-4" /> Ajouter
                </button>
            </div>

            {loading ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div> :
                <div className="bg-white border border-brand-line overflow-hidden">
                    <table className="w-full font-sans text-sm">
                        <thead className="bg-brand-cream text-brand-ink/60 ll-eyebrow">
                            <tr>
                                <th className="text-left p-4">Date</th>
                                <th className="text-left p-4">Type</th>
                                <th className="text-left p-4">Client</th>
                                <th className="text-left p-4">Pharmacien</th>
                                <th className="text-left p-4">Statut</th>
                                <th className="p-4 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="p-8 text-center text-brand-ink/50">Aucun rendez-vous</td></tr>
                            )}
                            {items.map((a) => (
                                <tr key={a.id} className="border-t border-brand-line hover:bg-brand-cream/50">
                                    <td className="p-4 text-brand-ink">{formatDateTime(a.start_at)}</td>
                                    <td className="p-4 text-brand-ink/80">{a.type_name}</td>
                                    <td className="p-4">
                                        <div className="text-brand-ink">{a.customer_name}</div>
                                        <div className="text-xs text-brand-ink/55">{a.customer_email}</div>
                                    </td>
                                    <td className="p-4 text-brand-ink/80">{a.pharmacist_name}</td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 text-xs rounded ${a.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                            {a.status === "confirmed" ? "Confirmé" : "Annulé"}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => setEditing(a)} className="text-brand-primary hover:opacity-70 mr-3"><Edit2 className="h-4 w-4" /></button>
                                        <button onClick={() => remove(a)} className="text-red-600 hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }

            {(editing || creating) && (
                <AppointmentDialog
                    item={editing}
                    types={types}
                    pharmacists={pharmacists}
                    onClose={() => { setEditing(null); setCreating(false); }}
                    onSaved={() => { setEditing(null); setCreating(false); load(); }}
                />
            )}
        </div>
    );
}

function AppointmentDialog({ item, types, pharmacists, onClose, onSaved }) {
    const isEdit = !!item;
    const initial = item ? {
        type_id: item.type_id,
        pharmacist_id: item.pharmacist_id,
        start_at: new Date(item.start_at).toISOString().slice(0, 16),
        customer_name: item.customer_name,
        customer_email: item.customer_email,
        customer_phone: item.customer_phone || "",
        notes: item.notes || "",
        status: item.status,
    } : {
        type_id: types[0]?.id || "",
        pharmacist_id: pharmacists[0]?.id || "",
        start_at: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        notes: "",
        status: "confirmed",
    };
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            const isoStart = new Date(form.start_at).toISOString();
            if (isEdit) {
                await api.put(`/admin/appointments/${item.id}`, {
                    type_id: form.type_id,
                    pharmacist_id: form.pharmacist_id,
                    start_at: isoStart,
                    customer_name: form.customer_name,
                    customer_email: form.customer_email,
                    customer_phone: form.customer_phone || null,
                    notes: form.notes || null,
                    status: form.status,
                });
                toast.success("Rendez-vous mis à jour");
            } else {
                await api.post(`/admin/appointments?pharmacist_id=${form.pharmacist_id}`, {
                    type_id: form.type_id,
                    start_at: isoStart,
                    customer_name: form.customer_name,
                    customer_email: form.customer_email,
                    customer_phone: form.customer_phone || null,
                    notes: form.notes || null,
                });
                toast.success("Rendez-vous créé");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-brand-line">
                    <h3 className="font-display text-xl text-brand-ink">
                        {isEdit ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
                    </h3>
                    <button onClick={onClose} className="text-brand-ink/60 hover:text-brand-ink"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-4 font-sans">
                    <Field label="Type">
                        <select value={form.type_id} onChange={upd("type_id")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary">
                            {types.map(t => <option key={t.id} value={t.id}>{t.name} ({t.duration_minutes}min)</option>)}
                        </select>
                    </Field>
                    <Field label="Pharmacien">
                        <select value={form.pharmacist_id} onChange={upd("pharmacist_id")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary">
                            {pharmacists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Date & heure">
                        <input type="datetime-local" value={form.start_at} onChange={upd("start_at")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary" />
                    </Field>
                    <Field label="Nom complet">
                        <input value={form.customer_name} onChange={upd("customer_name")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary" />
                    </Field>
                    <Field label="Courriel">
                        <input type="email" value={form.customer_email} onChange={upd("customer_email")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary" />
                    </Field>
                    <Field label="Téléphone">
                        <input value={form.customer_phone} onChange={upd("customer_phone")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary" />
                    </Field>
                    <Field label="Notes">
                        <textarea rows={3} value={form.notes} onChange={upd("notes")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary resize-none" />
                    </Field>
                    {isEdit && (
                        <Field label="Statut">
                            <select value={form.status} onChange={upd("status")} className="w-full bg-transparent border-b border-brand-line py-2 outline-none focus:border-brand-primary">
                                <option value="confirmed">Confirmé</option>
                                <option value="cancelled">Annulé</option>
                            </select>
                        </Field>
                    )}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-brand-line">
                    <button onClick={onClose} className="ll-btn ll-btn-ghost">Annuler</button>
                    <button onClick={save} disabled={saving} className="ll-btn ll-btn-primary disabled:opacity-60">
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Enregistrement…</> : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="ll-eyebrow text-brand-ink/60 block mb-1">{label}</label>
        {children}
    </div>
);
